import React, { useState } from 'react';
import { Grid, Button, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material'; // ★ 新增 Radio 組件
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { DataGrid } from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { apiUrl } from '../../apiUrl/ApiUrl';

export default function BuyerFilter({ data }) {
  const [needDate, setNeedDate] = useState(null);
  const [buyDate, setBuyDate] = useState(null);
  const [piNum, setPiNum] = useState('');
  const [orderNum, setOrderNum] = useState('');
  const [approveDate, setApproveDate] = useState(null);
  const [purchaseRows, setPurchaseRows] = useState(data?.buyerDtos || []);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ★ 新增：兩個查詢條件狀態
  const [signStatus, setSignStatus] = useState('全部');   // '全部' | '已簽核' | '未簽核'
  const [timeRange, setTimeRange]   = useState('近期');   // '全部' | '近期'

  const columns = [
    { field: 'date', headerName: '採購日期', width: 105, valueFormatter: params => params.value ? dayjs(params.value).format('YYYY/MM/DD') : '' },
    { field: 'manufacturer', headerName: '廠商', width: 85 },
    { field: 'needDate', headerName: '需求日', width: 105, valueFormatter: params => params.value ? dayjs(params.value).format('YYYY/MM/DD') : '' },
    { field: 'drawNum', headerName: '圖號', width: 160 },
    { field: 'itemName', headerName: '物品名', width: 300 },
    { field: 'unit', headerName: '單位', width: 60 },
    { field: 'amount', headerName: '數量', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(2) },
    { field: 'unitPrice', headerName: '單價', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(4) },
    { field: 'moneyAmount', headerName: '金額', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(2) },
    { field: 'orderNum', headerName: '單號', width: 110 },
    { field: 'piNum', headerName: '批號', width: 110 },
    { field: 'approve', headerName: '核准', width: 115 },
    { field: 'id', headerName: 'ID', width: 90 }
  ];

  const fetchMain = async () => {
    setLoading(true);
    try {
      const params = {
        needDate:    needDate    ? dayjs(needDate).format('YYYY-MM-DD')    : '',
        buyDate:     buyDate     ? dayjs(buyDate).format('YYYY-MM-DD')     : '',
        piNum,
        orderNum,
        approveDate: approveDate ? dayjs(approveDate).format('YYYY-MM-DD') : '',
        page: 1,

        // ★ 新增：把兩個條件帶到後端
        signStatus,  // '全部' | '已簽核' | '未簽核'
        timeRange,   // '全部' | '近期'
      };

      const res = await axios.get(`${apiUrl}/Buyer/GetMainsByFilter`, { params });
      setPurchaseRows(res.data.buyerDtos ?? []);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setNeedDate(null);
    setBuyDate(null);
    setApproveDate(null);
    setPiNum('');
    setOrderNum('');
    setSignStatus('全部'); // ★ 重置
    setTimeRange('近期');  // ★ 重置
    setPurchaseRows([]);
  };

  return (
    <div className='container-fluid'>
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* 查詢區（第一行） */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={2}>
          <DatePicker
            selected={needDate}
            onChange={date => setNeedDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="需求日"
            customInput={<TextField label="需求日" fullWidth size="small" />}
            isClearable
          />
        </Grid>
        <Grid item xs={2}>
          <DatePicker
            selected={buyDate}
            onChange={date => setBuyDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="採購日期"
            customInput={<TextField label="採購日期" fullWidth size="small" />}
            isClearable
          />
        </Grid>
        <Grid item xs={2}>
          <TextField label="批號" value={piNum} onChange={e => setPiNum(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={2}>
          <TextField label="單號" value={orderNum} onChange={e => setOrderNum(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={2}>
          <DatePicker
            selected={approveDate}
            onChange={date => setApproveDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="核准日期"
            customInput={<TextField label="核准日期" fullWidth size="small" />}
            isClearable
          />
        </Grid>
        <Grid item xs={1}>
          <Button variant="contained" onClick={fetchMain} fullWidth>查詢</Button>
        </Grid>
        <Grid item xs={1}>
          <Button variant="outlined" onClick={handleClear} fullWidth>清除</Button>
        </Grid>
      </Grid>

      {/* 查詢區（第二行：★ 新增兩個條件） */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {/* 已簽核 / 未簽核 / 全部 */}
        <Grid item xs={6} md={4}>
          <Grid container alignItems="center">
            <Grid item sx={{ mr: 2, fontSize: 14, color: 'text.secondary' }}>簽核狀態</Grid>
            <Grid item>
              <RadioGroup
                row
                value={signStatus}
                onChange={(e) => setSignStatus(e.target.value)}
              >
                <FormControlLabel value="全部"   control={<Radio size="small" />} label="全部" />
                <FormControlLabel value="已簽核" control={<Radio size="small" />} label="已簽核" />
                <FormControlLabel value="未簽核" control={<Radio size="small" />} label="未簽核" />
              </RadioGroup>
            </Grid>
          </Grid>
        </Grid>

        {/* 全部 / 近期 */}
        <Grid item xs={6} md={4}>
          <Grid container alignItems="center">
            <Grid item sx={{ mr: 2, fontSize: 14, color: 'text.secondary' }}>時間區間</Grid>
            <Grid item>
              <RadioGroup
                row
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <FormControlLabel value="全部" control={<Radio size="small" />} label="全部" />
                <FormControlLabel value="近期" control={<Radio size="small" />} label="近期" />
              </RadioGroup>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* 資料表 */}
      <div style={{ height: '70vh', width: '100%', marginBottom: 16 }}>
        <DataGrid
          rows={purchaseRows}
          columns={columns}
          loading={loading}
          pageSize={100}
          rowsPerPageOptions={[100]}
          pagination
          disableRowSelectionOnClick
          getRowId={row => row.id}
          rowHeight={30}
          headerHeight={32}
          sx={{ backgroundColor: '#fff' }}
          onCellDoubleClick={(params) => {
            setSelectedRow(params.row);
            setDialogOpen(true);
          }}
          columnVisibilityModel={{
            id: false,
          }}
        />
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>請確認是否駁回</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <div>
              <div>圖號：{selectedRow.drawNum}</div>
              <div>單號：{selectedRow.orderNum}</div>
              <div>需求日：{dayjs(selectedRow.needDate).format('YYYY/MM/DD')}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button
            onClick={async () => {
              if (!selectedRow) return;
              setLoading(true);
              try {
                await axios.put(
                  `${apiUrl}/Buyer/SignOffNo`,
                  {},
                  { params: { id: selectedRow.id, user: sessionStorage.getItem('userName') } }
                );
                fetchMain();
              } catch (err) {
                alert('駁回失敗');
              } finally {
                setLoading(false);
                setDialogOpen(false);
              }
            }}
            color="error" variant="contained"
          >
            駁回
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
